import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFire, FaArrowLeft, FaInfoCircle, FaMinus, FaEquals, FaPlus } from 'react-icons/fa';
import SEO, { PageSEO, StructuredData } from '../../components/SEO';

const TDEECalculator = () => {
  const [unit, setUnit] = useState('metric');
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('');
  const [height, setHeight] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weight, setWeight] = useState('');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [result, setResult] = useState(null);

  const activityLevels = [
    { value: 'sedentary', label: 'Sedentary', description: 'Little or no exercise, desk job', multiplier: 1.2 },
    { value: 'light', label: 'Lightly Active', description: 'Light exercise 1-3 days/week', multiplier: 1.375 },
    { value: 'moderate', label: 'Moderately Active', description: 'Moderate exercise 3-5 days/week', multiplier: 1.55 },
    { value: 'active', label: 'Very Active', description: 'Hard exercise 6-7 days/week', multiplier: 1.725 },
    { value: 'extreme', label: 'Extremely Active', description: 'Very hard exercise, physical job', multiplier: 1.9 },
  ];

  const calculateTDEE = (e) => {
    e.preventDefault();

    let heightInCm, weightInKg;

    if (unit === 'metric') {
      heightInCm = parseFloat(height);
      weightInKg = parseFloat(weight);
    } else {
      const totalInches = (parseFloat(heightFeet) * 12) + parseFloat(heightInches || 0);
      heightInCm = totalInches * 2.54;
      weightInKg = parseFloat(weight) * 0.453592;
    }

    // Mifflin-St Jeor Equation
    let bmr;
    if (gender === 'male') {
      bmr = (10 * weightInKg) + (6.25 * heightInCm) - (5 * parseFloat(age)) + 5;
    } else {
      bmr = (10 * weightInKg) + (6.25 * heightInCm) - (5 * parseFloat(age)) - 161;
    }

    const activity = activityLevels.find((a) => a.value === activityLevel);
    const tdee = bmr * activity.multiplier;

    setResult({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      deficit: Math.round(tdee - 500),
      mildDeficit: Math.round(tdee - 250),
      surplus: Math.round(tdee + 500),
      mildSurplus: Math.round(tdee + 250),
      activityLabel: activity.label,
    });
  };

  const resetCalculator = () => {
    setAge('');
    setHeight('');
    setHeightFeet('');
    setHeightInches('');
    setWeight('');
    setResult(null);
  };

  const tdeeStructuredData = StructuredData.calculator(
    'TDEE & Calorie Calculator',
    'Calculate your Total Daily Energy Expenditure and daily calorie needs for weight loss or gain.',
    '/calculators/tdee'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.tdeeCalculator} structuredData={tdeeStructuredData} />
      {/* Header */}
      <section className="bg-gradient-to-br from-orange-500 to-red-500 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/calculators" className="inline-flex items-center text-orange-100 hover:text-white mb-6 transition-colors">
            <FaArrowLeft className="mr-2" />
            All Calculators
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FaFire className="text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">TDEE & Calorie Calculator</h1>
              <p className="text-orange-100 mt-1">Find your daily calorie needs</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Your Details</h2>

            {/* Unit Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setUnit('metric')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'metric' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Metric
              </button>
              <button
                type="button"
                onClick={() => setUnit('imperial')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'imperial' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Imperial
              </button>
            </div>

            <form onSubmit={calculateTDEE} className="space-y-5">
              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setGender('male')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                      gender === 'male'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => setGender('female')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                      gender === 'female'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="e.g., 30"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                  min="15"
                  max="100"
                />
              </div>

              {/* Height */}
              {unit === 'metric' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="e.g., 170"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                    min="100"
                    max="250"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      placeholder="Feet"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                      min="3"
                      max="8"
                    />
                    <input
                      type="number"
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      placeholder="Inches"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      min="0"
                      max="11"
                    />
                  </div>
                </div>
              )}

              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'e.g., 70' : 'e.g., 154'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                  min="30"
                  max={unit === 'metric' ? '300' : '660'}
                />
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Level</label>
                <select
                  value={activityLevel}
                  onChange={(e) => setActivityLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  {activityLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label} - {level.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl hover:from-orange-600 hover:to-red-600 transition-colors font-semibold"
                >
                  Calculate TDEE
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={resetCalculator}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Results</h2>

            {result ? (
              <div className="space-y-6">
                {/* Main Results */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-sm text-gray-600 mb-1">BMR</div>
                    <div className="text-2xl font-bold text-gray-900">{result.bmr}</div>
                    <div className="text-xs text-gray-500">cal/day</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-center text-white">
                    <div className="text-sm text-orange-100 mb-1">TDEE</div>
                    <div className="text-2xl font-bold">{result.tdee}</div>
                    <div className="text-xs text-orange-100">cal/day</div>
                  </div>
                </div>

                {/* Calorie Goals */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Daily Calorie Goals</h3>

                  {/* Weight Loss */}
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                      <FaMinus />
                      <span>Weight Loss</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Mild (-0.25 kg/week):</span>
                        <span className="font-semibold text-gray-900 ml-2">{result.mildDeficit} cal</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Normal (-0.5 kg/week):</span>
                        <span className="font-semibold text-gray-900 ml-2">{result.deficit} cal</span>
                      </div>
                    </div>
                  </div>

                  {/* Maintenance */}
                  <div className="bg-gray-100 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                      <FaEquals />
                      <span>Maintenance</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Maintain weight:</span>
                      <span className="font-semibold text-gray-900 ml-2">{result.tdee} cal</span>
                    </div>
                  </div>

                  {/* Weight Gain */}
                  <div className="bg-green-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                      <FaPlus />
                      <span>Weight Gain</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Mild (+0.25 kg/week):</span>
                        <span className="font-semibold text-gray-900 ml-2">{result.mildSurplus} cal</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Normal (+0.5 kg/week):</span>
                        <span className="font-semibold text-gray-900 ml-2">{result.surplus} cal</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <Link
                  to="/pricing"
                  className="block w-full text-center bg-accent-500 text-white py-3 rounded-xl hover:bg-accent-600 transition-colors font-semibold"
                >
                  Get Personalized Meal Plan
                </Link>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaFire className="text-5xl mx-auto mb-4 text-gray-300" />
                <p>Enter your details to calculate your daily calorie needs</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FaInfoCircle className="text-orange-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">Understanding TDEE</h3>
          </div>
          <div className="prose prose-gray max-w-none">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What is BMR?</h4>
                <p className="text-sm text-gray-600">
                  Basal Metabolic Rate (BMR) is the number of calories your body burns at rest to maintain basic functions
                  like breathing, circulation, and cell production.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What is TDEE?</h4>
                <p className="text-sm text-gray-600">
                  Total Daily Energy Expenditure (TDEE) is your BMR plus calories burned through daily activities
                  and exercise. This is your maintenance calories.
                </p>
              </div>
            </div>
            <div className="mt-6 p-4 bg-orange-50 rounded-xl">
              <h4 className="font-semibold text-gray-900 mb-2">The Science</h4>
              <p className="text-sm text-gray-600">
                This calculator uses the Mifflin-St Jeor equation, which is considered the most accurate for estimating
                calorie needs. To lose weight, eat below your TDEE. To gain weight, eat above it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TDEECalculator;
